import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import { Edit as EditIcon, Person, Email, Phone, AccountCircle, CalendarToday, ExpandMore } from '@mui/icons-material';
import { countries } from './countryCodes';

function AccountSettings() {
    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '+90',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);

            setFormData({
                firstName: parsedUser.name || '',
                lastName: parsedUser.surname || '',
                email: parsedUser.email || '',
                phone: parsedUser.phoneNumber || '',
                countryCode: '+90',
                password: '',
                confirmPassword: ''
            });
        }
    }, []);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePassword = (password) => {
        return password.length >= 8;
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }

        // Real-time validation
        if (field === 'email' && value && !validateEmail(value)) {
            setErrors(prev => ({ ...prev, email: 'Geçerli bir e-posta adresi girin' }));
        }

        if (field === 'password' && value && !validatePassword(value)) {
            setErrors(prev => ({ ...prev, password: 'Şifre en az 8 karakter olmalıdır' }));
        }

        if (field === 'confirmPassword' && value && value !== formData.password) {
            setErrors(prev => ({ ...prev, confirmPassword: 'Şifreler eşleşmiyor' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Ad gereklidir';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Soyad gereklidir';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'E-posta gereklidir';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Geçerli bir e-posta adresi girin';
        }

        if (formData.password && !validatePassword(formData.password)) {
            newErrors.password = 'Şifre en az 8 karakter olmalıdır';
        }

        if (formData.password && formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Şifreler eşleşmiyor';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) return;

        const updatedUser = {
            ...user,
            name: formData.firstName,
            surname: formData.lastName,
            email: formData.email,
            phoneNumber: formData.phone
        };

        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        handleModeTransition(false);
    };

    const handleModeTransition = (toEdit) => {
        setIsTransitioning(true);
        setTimeout(() => {
            setIsEditing(toEdit);
            setIsTransitioning(false);
        }, 250);
    };

    const handleCancel = () => {
        setFormData({
            firstName: user.name || '',
            lastName: user.surname || '',
            email: user.email || '',
            phone: user.phoneNumber || '',
            countryCode: '+90',
            password: '',
            confirmPassword: ''
        });
        setErrors({});
        handleModeTransition(false);
    };

    return (
        <div className="min-h-screen relative">
            {/* Background Image */}
            <div
                className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url(/images/yacht-bg-3.jpg)`,
                    filter: "brightness(0.8) saturate(1.1)",
                }}
            />

            {/* Gradient Overlay */}
            <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />

            {/* Content */}
            <div className="relative z-10">
                <Navbar />

                {/* Hero Section */}
                <div className="relative pt-36 pb-12 text-center text-white">
                    <h1 className="text-5xl font-bold mb-4">
                        Hesap Ayarları
                    </h1>
                    <p className="text-lg text-white/60">
                        Hesap bilgilerinizi görüntüleyin ve düzenleyin
                    </p>
                </div>

                {/* Content Sections */}
                <div className="max-w-6xl mx-auto px-4 py-12">
                    {user ? (
                        <div className="space-y-8">
                            {!isEditing ? (
                                <div className={`bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 shadow-2xl max-w-4xl mx-auto transition-all duration-500 ease-in-out ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                                    <div className="flex justify-between items-center mb-8">
                                        <h2 className="text-3xl font-bold text-white">
                                            Hesap Bilgileri
                                        </h2>
                                        <button
                                            onClick={() => handleModeTransition(true)}
                                            className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
                                        >
                                            <EditIcon fontSize="medium" />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-6">
                                            <div className="flex items-center space-x-4">
                                                <div className="bg-blue-500/30 p-3 rounded-full">
                                                    <Person className="text-white" fontSize="medium" />
                                                </div>
                                                <div>
                                                    <p className="text-white/60 text-sm">Ad Soyad</p>
                                                    <p className="text-white text-lg font-medium">
                                                        {user.name && user.surname ? `${user.name} ${user.surname}` : (user.name || 'Belirtilmemiş')}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center space-x-4">
                                                <div className="bg-green-500/30 p-3 rounded-full">
                                                    <Email className="text-white" fontSize="medium" />
                                                </div>
                                                <div>
                                                    <p className="text-white/60 text-sm">E-posta</p>
                                                    <p className="text-white text-lg font-medium">{user.email || 'Belirtilmemiş'}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center space-x-4">
                                                <div className="bg-purple-500/30 p-3 rounded-full">
                                                    <Phone className="text-white" fontSize="medium" />
                                                </div>
                                                <div>
                                                    <p className="text-white/60 text-sm">Telefon</p>
                                                    <p className="text-white text-lg font-medium">{user.phoneNumber || 'Belirtilmemiş'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-6">
                                            <div className="flex items-center space-x-4">
                                                <div className="bg-orange-500/30 p-3 rounded-full">
                                                    <AccountCircle className="text-white" fontSize="medium" />
                                                </div>
                                                <div>
                                                    <p className="text-white/60 text-sm">Kullanıcı Türü</p>
                                                    <p className="text-white text-lg font-medium">
                                                        {user.userType === 2 ? 'Admin' : 'Standart Kullanıcı'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center space-x-4">
                                                <div className="bg-cyan-500/30 p-3 rounded-full">
                                                    <CalendarToday className="text-white" fontSize="medium" />
                                                </div>
                                                <div>
                                                    <p className="text-white/60 text-sm">Üyelik Tarihi</p>
                                                    <p className="text-white text-lg font-medium">
                                                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className={`bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl transition-all duration-500 ease-in-out ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                                    <h2 className="text-3xl font-bold text-white mb-8 text-center">
                                        Hesap Bilgilerini Düzenle
                                    </h2>

                                    <form className="space-y-6 max-w-2xl mx-auto">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-white font-medium mb-2">
                                                    Ad *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.firstName}
                                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                                    placeholder="Adınızı girin"
                                                />
                                                {errors.firstName && (
                                                    <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-white font-medium mb-2">
                                                    Soyad *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.lastName}
                                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                                    placeholder="Soyadınızı girin"
                                                />
                                                {errors.lastName && (
                                                    <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-white font-medium mb-2">
                                                E-posta *
                                            </label>
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                                placeholder="E-posta adresinizi girin"
                                            />
                                            {errors.email && (
                                                <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-white font-medium mb-2 flex items-center gap-2">
                                                <Phone fontSize="small" />
                                                Telefon (Opsiyonel)
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                                        className="flex items-center gap-2 bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white hover:bg-white/15 focus:outline-none focus:border-blue-400 transition-all duration-300 min-w-[120px]"
                                                    >
                                                        <span>{countries.find(c => c.code === formData.countryCode)?.flag || '🇹🇷'}</span>
                                                        <span>{formData.countryCode}</span>
                                                        <ExpandMore fontSize="small" />
                                                    </button>

                                                    {showCountryDropdown && (
                                                        <div className="absolute top-full left-0 mt-1 w-64 max-h-48 overflow-y-auto bg-white/95 backdrop-blur-lg border border-white/20 rounded-xl shadow-2xl z-50">
                                                            {countries.map((country) => (
                                                                <button
                                                                    key={country.code}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        handleInputChange('countryCode', country.code);
                                                                        setShowCountryDropdown(false);
                                                                    }}
                                                                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-800 hover:bg-blue-500/20 transition-colors duration-200 text-left"
                                                                >
                                                                    <span className="text-lg">{country.flag}</span>
                                                                    <span className="font-medium">{country.code}</span>
                                                                    <span className="text-sm text-gray-600">{country.name}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                <input
                                                    type="tel"
                                                    value={formData.phone}
                                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                                    className="flex-1 bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                                    placeholder="5XX XXX XX XX"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-white font-medium mb-2">
                                                    Yeni Şifre (Opsiyonel)
                                                </label>
                                                <input
                                                    type="password"
                                                    value={formData.password}
                                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                                    placeholder="En az 8 karakter"
                                                />
                                                {errors.password && (
                                                    <p className="text-red-400 text-sm mt-1">{errors.password}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-white font-medium mb-2">
                                                    Şifre Tekrar (Opsiyonel)
                                                </label>
                                                <input
                                                    type="password"
                                                    value={formData.confirmPassword}
                                                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                                    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                                    placeholder="Şifrenizi tekrar girin"
                                                />
                                                {errors.confirmPassword && (
                                                    <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-4 justify-center pt-6">
                                            <button
                                                type="button"
                                                onClick={handleSave}
                                                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 rounded-full font-semibold shadow-xl transform hover:scale-105 transition-all duration-300"
                                            >
                                                Değişiklikleri Kaydet
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCancel}
                                                className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-8 py-3 rounded-full font-semibold shadow-xl transform hover:scale-105 transition-all duration-300"
                                            >
                                                İptal
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl text-center">
                            <p className="text-white/80 text-xl">Kullanıcı bilgileri yüklenemedi.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AccountSettings;